import type Phaser from 'phaser';
import type { Vec2 } from '../core/types';
import { JOYSTICK_RADIUS, joystickKnob, joystickVector } from './joystick';

export interface TouchControls {
  /** Mode tactile actif : écran à pointeur grossier, ou un toucher a été vu. */
  enabled(): boolean;
  /** Déplacement du joystick virtuel (nul s'il n'est pas touché). */
  move(): Vec2;
  /** Vrai une seule fois après un appui sur la moitié droite de l'écran. */
  consumeDodge(): boolean;
  /** Redessine le joystick ; à appeler à chaque image. */
  update(): void;
  dispose(): void;
}

export function isCoarsePointer(): boolean {
  try { return window.matchMedia('(pointer: coarse)').matches; } catch { return false; }
}

const DEPTH = 100000;

export function createTouchControls(scene: Phaser.Scene): TouchControls {
  const coarse = isCoarsePointer();
  let sawTouch = false;
  let joyId: number | null = null;
  let base: Vec2 = { x: 0, y: 0 };
  let current: Vec2 = { x: 0, y: 0 };
  let dodgeQueued = false;
  // pointeurs par défaut : souris + 1 toucher ; on en ajoute 2 (une seule fois, les pointeurs survivent aux scènes)
  const have = scene.input.manager.pointers.length;
  if (have < 4) scene.input.addPointer(4 - have);
  const gfx = scene.add.graphics().setScrollFactor(0).setDepth(DEPTH);

  const enabled = (): boolean => coarse || sawTouch;
  const onDown = (p: Phaser.Input.Pointer): void => {
    if (p.wasTouch) sawTouch = true;
    if (!p.wasTouch || !enabled()) return;
    if (p.x < scene.scale.width / 2) {
      if (joyId !== null) return;
      joyId = p.id;
      base = { x: p.x, y: p.y };
      current = { x: p.x, y: p.y };
    } else {
      dodgeQueued = true;
    }
  };
  const onMove = (p: Phaser.Input.Pointer): void => {
    if (p.id === joyId) { current = { x: p.x, y: p.y }; return; }
    // souris reprise sur un portable tactile ; ignorée tant qu'un doigt tient le joystick
    if (!p.wasTouch && !coarse && joyId === null) sawTouch = false;
  };
  const release = (p: Phaser.Input.Pointer): void => { if (p.id === joyId) joyId = null; };
  scene.input.on('pointerdown', onDown);
  scene.input.on('pointermove', onMove);
  scene.input.on('pointerup', release);
  scene.input.on('pointerupoutside', release);
  // 'gameout' n'est volontairement pas écouté : il est émis aussi pour les doigts et ne doit pas lâcher le joystick ;
  // un doigt réellement levé est détecté par pointerup ou par le filet de sécurité de update().

  return {
    enabled,
    move: () => (joyId === null ? { x: 0, y: 0 } : joystickVector(base, current)),
    consumeDodge() { const d = dodgeQueued; dodgeQueued = false; return d; },
    update() {
      // filet de sécurité : doigt levé sans événement pointerup (touchcancel, perte de focus)
      if (joyId !== null && !scene.input.manager.pointers.some((p) => p.id === joyId && p.isDown)) joyId = null;
      gfx.clear();
      if (joyId === null) return;
      const knob = joystickKnob(base, current);
      gfx.fillStyle(0x1b140c, 0.35).fillCircle(base.x, base.y, JOYSTICK_RADIUS);
      gfx.lineStyle(5, 0x1b140c, 0.7).strokeCircle(base.x, base.y, JOYSTICK_RADIUS);
      gfx.lineStyle(3, 0xffffff, 0.85).strokeCircle(base.x, base.y, JOYSTICK_RADIUS);
      gfx.fillStyle(0xffa040, 0.9).fillCircle(knob.x, knob.y, 26);
      gfx.lineStyle(3, 0x3a2a16, 1).strokeCircle(knob.x, knob.y, 26);
    },
    dispose() {
      scene.input.off('pointerdown', onDown);
      scene.input.off('pointermove', onMove);
      scene.input.off('pointerup', release);
      scene.input.off('pointerupoutside', release);
      gfx.destroy();
    },
  };
}
